import '../../types/grid-overrides.d.ts'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import {
  type ColDef,
  type FirstDataRenderedEvent,
  type GetContextMenuItemsParams,
  type GetRowIdParams,
  type GridOptions as AgGridOptions,
  type HeaderValueGetterParams,
  type IServerSideDatasource as IAgGridServerSideDatasource,
  type MenuItemDef,
  Grid,
  ModuleRegistry,
  type StoreRefreshedEvent,
  type IServerSideGetRowsParams,
  type ChartModelType,
  type GridApi,
  type ValueFormatterFunc,
  type ColGroupDef,
  type ChartModel,
  type ColumnApi,
  type UpdateChartParams,
  type CellRangeParams,
  type Column,
  type UpdateRangeChartParams,
  type GridReadyEvent,
  type ColumnState,
  type IMultiFilterDef,
  type SetFilterModel,
  type NumberFilterModel,
  type TextFilterModel,
  type IMultiFilterModel,
} from '@ag-grid-community/core'
import { CsvExportModule } from '@ag-grid-community/csv-export'
import { GridChartsModule } from '@ag-grid-enterprise/charts'
import { ClipboardModule } from '@ag-grid-enterprise/clipboard'
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel'
import { LicenseManager } from '@ag-grid-enterprise/core'
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { MultiFilterModule } from '@ag-grid-enterprise/multi-filter'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { SetFilterModule } from '@ag-grid-enterprise/set-filter'
import { StatusBarModule } from '@ag-grid-enterprise/status-bar'

import {
  useRef,
  useMemo,
  useEffect,
  memo,
  forwardRef,
  type MutableRefObject,
  type RefAttributes,
  type Ref,
  type RefObject,
  type CSSProperties,
} from 'react'
import { Box, type BoxProps } from '../Box'

import { resolveChartThemeOverrides } from './chart/embeddedChartUtils'
import {
  chartThemes,
  customChartThemes,
  chartThemeOverrides,
} from './chart/themes'
import {
  getChartTheme,
  getCurrentChartTheme,
  onColorSchemeUpdate,
} from './chart/themeUtils'
import {
  addressColumnType,
  urlColumnType,
  chainColumnType,
  collectionColumnType,
  numberColumnType,
  projectColumnType,
  trendingColumnType,
  trendingInverseColumnType,
  unknownColumnType,
  rowIndexColumnType,
  contractColumnType,
  marketSectorColumnType,
  dateTimeColumnType,
  dateColumnType,
  transactionHashColumnType,
  cohortColumnType,
  currencyColorizedColumnType,
  buyAndSellColumnType,
} from './columnTypes'
import { publicDataTypes } from './dataTypes'
import { suppressHeadingCss } from './grid-theme.css'
import { generateSprite, icons } from './icons'
import { HeaderRenderer } from './renderers/HeaderRenderer'
import { SubHeaderRenderer } from './renderers/SubHeaderRenderer'
// import { LoadingCellRenderer } from "./renderers/LoadingCellRenderer"

// side-effect
import '@ag-grid-community/styles/ag-grid-no-native-widgets.css'

export type * from '@ag-grid-community/core'

export type * from './dataTypes'

LicenseManager.setLicenseKey(
  'Using_this_AG_Grid_Enterprise_key_( AG-045200 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Token Terminal Oy )_is_granted_a_( Multiple Applications )_Developer_License_for_( 1 ))_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_AG_Grid_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_( 1 )_Production_Environment___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 16 July 2024 )____[v2]_MTcyMTA4NDQwMDAwMA==ebf81de2506840e08c27fa831cc6b877',
)

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClipboardModule,
  MenuModule,
  CsvExportModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  ServerSideRowModelModule,
  RangeSelectionModule,
  StatusBarModule,
  ExcelExportModule,
  GridChartsModule,
  SetFilterModule,
  MultiFilterModule,
])

export type GridOptions<T = any> = Omit<
  AgGridOptions<T>,
  'api' | 'datasource' | 'getRowId'
>

export type DataGridState = {
  expandedRows?: Array<string>
  columns: Array<ColumnState>
  filters?: Record<
    string,
    SetFilterModel | NumberFilterModel | TextFilterModel | IMultiFilterModel
  >
  quickFilter?: string
  charts?: Array<Omit<ChartModel, 'chartOptions' | 'chartPalette'>>
}

export type DatatableApi = {
  refresh(purge?: boolean): void
  updateContext(context: Record<string, unknown>): void
  updateRefData<T extends Record<string, unknown>, TValue = any>(
    columnName: string,
    data: T,
    valueFormatter?: ValueFormatterFunc<T, TValue>,
  ): void
  getState(): DataGridState
  applyState(state: DataGridState, reset?: boolean): Promise<void>
  resetState(): Promise<void>
}

export interface IServerSideDatasource<
  T extends Record<string, unknown>,
  C = {},
> extends IAgGridServerSideDatasource {
  /**
   * Grid calls `getRows` when it requires more rows as specified in the params.
   * Params object contains callbacks for responding to the request.
   */
  getRows(params: IServerSideGetRowsParams<T, C>): void
  /** Optional method, if your datasource has state it needs to clean up. */
  destroy?(): void
}

export type DataTableProps<
  T extends Record<string, unknown>,
  C = {},
> = GridOptions<T> & {
  api?: MutableRefObject<DatatableApi | null>
  initialContext?: C
  minCols?: number
  onStoreUpdated?: GridOptions<T>['onStoreRefreshed']
  height?: string | number
  getRowId?: (data: GetRowIdParams<T>) => string
  data?: Array<T>
  datasource?: IServerSideDatasource<T, C>
  bleedX?: BoxProps['bleedX']
  bleedY?: BoxProps['bleedY']
  allowExternalLinks?: boolean
  initialSideBar?: 'columns' | 'filters' | null
  suppressGroupHeader?: boolean
}

const CHART_EXCLUDED_CHART_DATA_TYPES = ['excluded', 'category']

const CHART_VALUE_CELL_DATA_TYPES = [
  'number',
  'currency',
  'trend',
  'change',
  'percentage',
  'time',
]

const isChartEnabledValueColumn = (column: Column): boolean => {
  const colDef = column.getColDef()
  if (CHART_EXCLUDED_CHART_DATA_TYPES.includes(colDef?.chartDataType ?? '')) {
    return false
  }
  // Exclude columns like categories (project name, market sector etc) and group columns (cellDataType false)
  return (
    typeof colDef.cellDataType === 'string' &&
    CHART_VALUE_CELL_DATA_TYPES.includes(colDef.cellDataType)
  )
}

function isColGroup(columnDef: ColDef | ColGroupDef): columnDef is ColGroupDef {
  return (
    (columnDef as ColGroupDef).children &&
    Array.isArray((columnDef as ColGroupDef).children)
  )
}

function augmentedColumnDef(columnDef: ColDef | ColGroupDef) {
  // @ts-ignore - base types
  if (columnDef?.headerComponentParams?.subHeader) {
    return {
      headerComponent: SubHeaderRenderer,
      headerValueGetter: (params: HeaderValueGetterParams) => {
        // @ts-ignore - bad type
        return `${params.colDef.headerName} (${columnDef?.headerComponentParams?.subHeader})`
      },
      ...columnDef,
    }
  }

  if (isColGroup(columnDef)) {
    ;(columnDef as ColGroupDef).children = (
      columnDef as ColGroupDef
    ).children.map((childDef) => {
      return augmentedColumnDef(childDef)
    })
  } else {
    return {
      headerComponent: HeaderRenderer,
      ...columnDef,
    } as ColDef<any>
  }

  return columnDef
}

const resolveChartUpdateModel = (
  model: ChartModelType,
): 'pivotChartUpdate' | 'rangeChartUpdate' => {
  return `${model}ChartUpdate`
}

/*
 * We need to call this any time chart's data is created or updated.
 * The main reason we need this is because AG Grid doesn't pass sufficient or any context to things like
 * chart axis formatter. We need the context so we can fix things like
 * - "6000000" instead of "$6m" (using column data type specific valueFormatter) in axis
 * - "Latest" instead of "Price latest" (using parent column def)
 */
type UpdateParams = { cellRange?: CellRangeParams }
const resolveChartUpdateParams = (
  chart: Pick<ChartModel, 'chartId' | 'cellRange' | 'modelType'>,
  columnApi: ColumnApi,
  { cellRange: nextCellRange }: UpdateParams = {},
): UpdateChartParams => {
  const { modelType, cellRange: chartCellRange, chartId } = chart
  const cellRange = nextCellRange ?? chartCellRange
  const { columns: chartColumnIds } = cellRange
  const columns = columnApi.getAllDisplayedColumns()

  // Find value columns displayed in the chart. Filters out columns like "Project name" and whatnot
  const chartValueColumns = columns.filter(
    (column) =>
      chartColumnIds?.includes(column.getColId()) &&
      isChartEnabledValueColumn(column),
  )
  // Consider first value column as primary (refers to primary axis). Can be undefined when cellRange has no value cells
  const [primaryValueColumn] = chartValueColumns

  const { initialAggFunc, defaultAggFunc, aggFunc } =
    primaryValueColumn?.getColDef() ?? {}

  const params: UpdateChartParams = {
    type: resolveChartUpdateModel(modelType), // At least pivot chart needs this to be set on creation
    chartId,
    chartThemeOverrides: resolveChartThemeOverrides(
      chartThemeOverrides,
      chartValueColumns,
    ),
  }

  if (modelType === 'pivot') {
    return params
  }
  // Dont pass cellRange to pivot chart or the update breaks and theme wont be set
  ;(params as UpdateRangeChartParams).cellRange = cellRange
  // Same here. Also no idea why there are both initial and default in use, but support all
  ;(params as UpdateRangeChartParams).aggFunc =
    aggFunc ?? initialAggFunc ?? defaultAggFunc ?? 'sum'

  return params
}

function DataTableInner<
  T extends Record<string, unknown>,
  C = Record<string, unknown>,
>(
  {
    columnDefs = [],
    defaultColDef = {
      sortable: true,
      resizable: true,
      lockPinned: true,
      width: 150,
      unSortIcon: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      },
      floatingFilter: true,
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
    },
    api,
    data,
    datasource,
    initialContext,
    minCols = 25,
    onGridReady,
    onStoreUpdated,
    height = `100%`,
    bleedX,
    bleedY,
    allowExternalLinks,
    onFirstDataRendered,
    suppressExcelExport,
    suppressCsvExport,
    style,
    initialSideBar = 'columns',
    suppressGroupHeader,
    onChartCreatedEvent,
    ...options
  }: DataTableProps<T, C> &
    RefAttributes<HTMLDivElement> & {
      style?: CSSProperties
      onChartCreatedEvent?: () => void
    },
  ref: Ref<HTMLDivElement>,
) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const elRef = ref || useRef<HTMLDivElement>(null)
  const currentGridOptions = useRef<AgGridOptions | null>(null)
  const previousData = useRef<Array<T> | null>(null)
  const openedRowGroups = useRef<Set<string>>(new Set())

  const initialContextRef = useRef({
    ...initialContext,
    allowExternalLinks,
  })
  const gridOptions = useMemo<AgGridOptions<T>>(() => {
    const augmentedColumnDefs = (columnDefs ?? []).map((columnDef) => {
      return augmentedColumnDef(columnDef)
    })

    // check if better way?
    if (
      currentGridOptions.current?.api &&
      currentGridOptions.current?.columnApi
    ) {
      const currentColumnWidths: Array<{ key: string; newWidth: number }> = []
      currentGridOptions.current.columnApi.getColumnState().forEach((state) => {
        if (state.width) {
          currentColumnWidths.push({
            key: state.colId,
            newWidth: state.width,
          })
        }
      })
      currentGridOptions.current.api.setColumnDefs(augmentedColumnDefs)
      // restore widths
      currentGridOptions.current?.columnApi?.setColumnWidths(
        currentColumnWidths,
      )
    }

    return {
      rowGroupPanelShow: 'always',
      enterNavigatesVertically: true,
      enableRangeSelection: true,
      enableFillHandle: false,
      rowSelection: 'multiple',
      suppressRowClickSelection: true,
      groupSelectsChildren: false,
      rowBuffer: 50,
      autoGroupColumnDef: {
        minWidth: 200,
        cellRendererParams: {
          suppressCount: true,
          checkbox: false,
        },
        headerComponent: HeaderRenderer,
        // group columns configured to use the Group Column Filter
        filter: 'agGroupColumnFilter',
        ...options.autoGroupColumnDef,
      },
      groupDisplayType: 'multipleColumns',
      suppressColumnVirtualisation: true,
      suppressCsvExport,
      suppressExcelExport,
      suppressMenuHide: true,
      statusBar: {
        statusPanels: [
          { statusPanel: 'agSelectedRowCountComponent', align: 'left' },
          { statusPanel: 'agAggregationComponent', align: 'right' },
        ],
      },
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              syncLayoutWithGrid: true,
              suppressPivotMode: true,
            },
          },
          {
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
            toolPanelParams: {
              syncLayoutWithGrid: true,
            },
          },
        ],
        position: 'right',
        defaultToolPanel: initialSideBar ?? undefined,
      },
      ...options,
      excelStyles: [
        {
          id: 'dateType',
          dataType: 'DateTime',
          numberFormat: {
            format: 'yyy-mm-ddThh:mm:ss',
          },
        },
        {
          id: 'addressType',
          dataType: 'String',
        },
        {
          id: 'transactionHashType',
          dataType: 'String',
        },
        ...(options.excelStyles ?? []),
      ],
      context: initialContextRef.current,
      columnDefs: augmentedColumnDefs,
      defaultColDef,
      dataTypeDefinitions: publicDataTypes,
      columnTypes: {
        numberColumn: numberColumnType as ColDef<T, number>,
        trendingColumn: trendingColumnType as ColDef<T, number>,
        trendingInverseColumn: trendingInverseColumnType as ColDef<T, number>,
        projectColumn: projectColumnType as ColDef<T, string>,
        chainColumn: chainColumnType as ColDef<T, string>,
        unknownColumn: unknownColumnType as ColDef<T, string>,
        addressColumn: addressColumnType as ColDef<T, string>,
        transactionHashColumn: transactionHashColumnType as ColDef<T, string>,
        urlColumn: urlColumnType as ColDef<T, string>,
        collectionColumn: collectionColumnType as ColDef<T, string>,
        contractColumn: contractColumnType as ColDef<T, string>,
        rowIndexColumn: rowIndexColumnType as ColDef<T, number>,
        marketSectorColumn: marketSectorColumnType as ColDef<T, string>,
        dateTimeColumn: dateTimeColumnType as ColDef<T, Date>,
        dateColumn: dateColumnType as ColDef<T, Date>,
        cohortColumn: cohortColumnType as ColDef<T, number>,
        currencyColorizedColumn: currencyColorizedColumnType as ColDef<
          T,
          number
        >,
        buyAndSellColumn: buyAndSellColumnType as ColDef<T, string>,
      },
      // loadingCellRenderer: LoadingCellRenderer,
      getContextMenuItems,
      icons,
      chartThemes,
      chartThemeOverrides,
      customChartThemes,
      onChartCreated({ chartId, api, columnApi }) {
        onChartCreatedEvent?.()

        const chart = api
          .getChartModels()
          ?.find(({ chartId: id }) => id === chartId)

        // formality check maybe.
        if (chart) {
          api.updateChart({
            ...resolveChartUpdateParams(chart, columnApi),
            chartThemeName: getCurrentChartTheme(),
          })
        }
      },
      onChartRangeSelectionChanged({ chartId, api, columnApi, cellRange }) {
        const chart = api
          .getChartModels()
          ?.find(({ chartId: id }) => id === chartId)

        if (chart) {
          api.updateChart(
            resolveChartUpdateParams(chart, columnApi, { cellRange }),
          )
        }
      },
      onGridReady(params: GridReadyEvent) {
        if (params.api.getSideBar()?.toolPanels?.length) {
          const columnsToolPanel = params.api.getToolPanelInstance('columns')
          columnsToolPanel?.collapseColumnGroups()
        }

        onGridReady?.(params)
      },
      onFirstDataRendered(params: FirstDataRenderedEvent) {
        const allColumnIds: string[] = []
        params.columnApi!.getColumns()!.forEach((column) => {
          if (!column.getUserProvidedColDef()?.width) {
            allColumnIds.push(column.getId())
          }
        })
        gridOptions.columnApi!.autoSizeColumns(allColumnIds)

        onFirstDataRendered?.(params)
      },
      onRowGroupOpened: (params) => {
        if (params.node.expanded) {
          if (params.node.key) {
            openedRowGroups.current.add(params.node.key)
          }
        } else {
          if (params.node.key) {
            openedRowGroups.current.delete(params.node.key)
          }
        }
      },
      isServerSideGroupOpenByDefault: function (params) {
        if (params.rowNode.key) {
          return openedRowGroups.current.has(params.rowNode.key)
        }

        return false
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs])

  // if grid api is available
  if (currentGridOptions.current) {
    const gridApi = currentGridOptions.current!.api!
    // @ts-ignore - use private gridOptionsService
    gridApi.gridOptionsService.set('suppressExcelExport', suppressExcelExport)
    // @ts-ignore - use private gridOptionsService
    gridApi.gridOptionsService.set('suppressCsvExport', suppressCsvExport)

    if (previousData.current !== data && data?.length) {
      gridApi.setRowData(data)
      previousData.current = data
    }
  }

  useEffect(() => {
    const spriteExists = document.querySelector('#datatable-sprite')
    if (!spriteExists) {
      const sprite = generateSprite()
      requestAnimationFrame(() => {
        document.body.appendChild(sprite)
      })
    }

    if (datasource) {
      gridOptions.rowModelType = 'serverSide'
      // @ts-ignore
      gridOptions.onStoreUpdated = (params: StoreRefreshedEvent) => {
        onStoreUpdated?.(params)
      }
      gridOptions.onSortChanged = (params) => {
        params.api.ensureIndexVisible(0, 'top')
      }
      gridOptions.onFilterChanged = (params) => {
        params.api.ensureIndexVisible(0, 'top')
      }
    }

    const unused = new Grid(
      (elRef as RefObject<HTMLElement>).current!,
      gridOptions,
    )
    currentGridOptions.current = gridOptions
    const gridApi: GridApi<T> = (gridOptions as Grid['gridOptions']).api

    // Update possible currently opened charts' color themes
    const cleanupColorSchemeListener = onColorSchemeUpdate((mode) => {
      const charts = currentGridOptions.current?.api?.getChartModels()
      charts?.forEach(({ modelType, chartId }) => {
        currentGridOptions.current?.api?.updateChart({
          type: resolveChartUpdateModel(modelType),
          chartId,
          chartThemeName: getChartTheme(mode),
        })
      })
    })

    // create external api
    if (api) {
      api.current = {
        refresh(purge = true) {
          if (currentGridOptions.current?.api) {
            if (datasource) {
              currentGridOptions.current.api!.refreshServerSide({
                purge,
              })
            } else {
              currentGridOptions.current.api!.refreshCells()
              currentGridOptions.current.api!.refreshHeader()
            }
          }
        },
        updateContext(context) {
          if (currentGridOptions.current?.api) {
            currentGridOptions.current.context = {
              ...currentGridOptions.current.context,
              ...context,
            }

            if (datasource) {
              currentGridOptions.current.api!.refreshServerSide({
                purge: true,
              })
            } else {
              currentGridOptions.current.api!.refreshCells()
              currentGridOptions.current.api!.refreshHeader()
            }
          }
        },
        updateRefData(columnName, data, valueFormatter = undefined) {
          const column =
            currentGridOptions.current?.columnApi?.getColumn(columnName)

          if (column) {
            const colDef = column.getColDef()

            const colDefWithRefData: ColDef = {
              ...colDef,
              refData: data as { [key: string]: string },
              filterParams: {
                ...colDef.filterParams,
                valueFormatter,
              },
            }

            /*
             * Handle multi filter
             * This allows use to use filterParams.values in col defs as we would use with plain "agSetColumnFilter" and
             * respect the column type's definitions :fingerscrossed:
             */
            if (colDef.filter === 'agMultiColumnFilter') {
              colDefWithRefData.filterParams.filters = (
                colDef.filterParams.filters as Array<IMultiFilterDef>
              ).map((filterDef, i) => {
                if (filterDef.filter !== 'agSetColumnFilter') {
                  return {
                    ...filterDef,
                    filterParams: {
                      ...filterDef.filterParams,
                      valueFormatter,
                    },
                  }
                }
                // else: "Set column" -filter for multi filter

                const { filters, ...colDefFilterParams } =
                  colDefWithRefData.filterParams

                return {
                  ...filterDef,
                  filterParams: {
                    // In practice not needed, but to be thorough
                    ...filterDef.filterParams,
                    // Get eg. cell renderer
                    ...colDefFilterParams,
                    // "filters" are the same, so should be in same order anyway. Comes from collectionType
                    ...filters[i].filterParams,
                    valueFormatter,
                  },
                }
              })

              /*
               * If ref data using column is also in initial grouping, AG Grid spawns auto column for it to be used.
               * That works as is as long as it's not multi filter, in which case it causes grid to fail because:
               *
               * "Set Filter cannot initialise because you are using a row model that does not contain all rows in the browser. Either use a different filter type, or configure Set Filter such that you provide it with values"
               *
               * Solution: update filterParams for it as well.
               */

              const autoColumn =
                currentGridOptions.current?.columnApi?.getColumn(
                  `ag-Grid-AutoColumn-${columnName}`,
                )

              if (autoColumn) {
                const autoColDef = autoColumn.getColDef()

                // NOTE: there is a BUG that this still doesn't use the correct cellRenderer for the filter itself when mart is grouped by multi filter initially. Nothing I've tried fixes it
                const autoColDefWithRefData: ColDef = {
                  ...autoColDef,
                  refData: data as { [key: string]: string },
                  filterParams: {
                    ...autoColDef.filterParams,
                    filters: colDefWithRefData.filterParams.filters,
                  },
                }

                autoColumn.setColDef(
                  autoColDefWithRefData,
                  // This is always null, but I guess this is more correct
                  autoColumn.getUserProvidedColDef(),
                )
              }
            }

            column.setColDef(colDefWithRefData, column.getUserProvidedColDef())
          }

          currentGridOptions.current?.api?.refreshCells({ force: true })
          currentGridOptions.current?.api?.refreshHeader()
        },
        getState(): DataGridState {
          const columns =
            currentGridOptions.current?.columnApi?.getColumnState() ?? []
          const chartModels = currentGridOptions.current?.api?.getChartModels()
          const filters = currentGridOptions.current?.api?.getFilterModel()
          // Prevent possible empty string
          const quickFilter =
            currentGridOptions.current?.api?.getQuickFilter() || undefined

          return {
            expandedRows: Array.from(openedRowGroups.current),
            columns,
            // Lighten the payload to strip out eg. theme related things, which might be in unsync when preset re-used. We deduce these based on app state basically
            charts: chartModels?.map(
              ({ chartOptions, chartPalette, ...chart }) => chart,
            ),
            filters,
            quickFilter,
          }
        },
        async applyState(
          { columns, filters, quickFilter, charts, expandedRows },
          reset = true,
        ) {
          const gridApi = currentGridOptions.current?.api
          const columnApi = currentGridOptions.current?.columnApi
          openedRowGroups.current = new Set(expandedRows ?? [])

          return new Promise((resolve) => {
            requestAnimationFrame(() => {
              const columnsAsMap = new Map(
                (columns || []).map((column) => {
                  return [column.colId, column]
                }),
              )

              if (reset && columnApi) {
                columnApi.getAllDisplayedColumns().forEach((column) => {
                  const colState = columnsAsMap.has(column.getColId())
                    ? columnsAsMap.get(column.getColId())
                    : {}

                  columnsAsMap.set(column.getColId(), {
                    colId: column.getColId(),
                    width: 150,
                    hide: true,
                    pinned: null,
                    sort: null,
                    sortIndex: null,
                    aggFunc: null,
                    rowGroup: false,
                    rowGroupIndex: null,
                    pivot: false,
                    pivotIndex: null,
                    flex: null,
                    ...colState,
                  })
                })
              }
              if (columnApi && columns?.length) {
                // Always true in practice
                columnApi.applyColumnState({
                  state: Array.from(columnsAsMap.values()),
                  applyOrder: true,
                })
              }

              if (gridApi) {
                if (filters) {
                  gridApi.setFilterModel(filters)
                }
                if (quickFilter) {
                  gridApi.setQuickFilter(quickFilter)
                }
              }

              if (charts?.length && columnApi && gridApi) {
                const getChart = (
                  partialChartModel: (typeof charts)[number],
                ): ChartModel => {
                  const { columns: chartColumnIds } =
                    partialChartModel.cellRange
                  const columns = columnApi.getAllDisplayedColumns()

                  // Find value columns displayed in the chart.
                  const chartValueColumns = columns.filter(
                    (column) =>
                      chartColumnIds?.includes(column.getColId()) &&
                      isChartEnabledValueColumn(column),
                  )

                  const overrides = resolveChartThemeOverrides(
                    chartThemeOverrides,
                    chartValueColumns,
                  )
                  const chartTheme = getCurrentChartTheme()

                  return {
                    ...partialChartModel,
                    chartThemeName: chartTheme,
                    chartOptions: overrides,
                    chartPalette: customChartThemes[chartTheme].palette,
                  }
                }

                const restoredCharts: ChartModel[] = []

                requestAnimationFrame(() => {
                  charts.forEach((partialChartModel) => {
                    const chartModel = getChart(partialChartModel)
                    // Restoring charts themselves will do little if the colums have been changed too
                    const newChart = gridApi?.restoreChart(chartModel)
                    if (newChart) {
                      restoredCharts.push({
                        ...chartModel,
                        chartId: newChart.chartId,
                      })
                    }
                  })
                })

                const updateCharts = () => {
                  requestAnimationFrame(() => {
                    restoredCharts.forEach((chart) => {
                      // This will update the chart to draw correct data. Sort of a refresh
                      gridApi.updateChart(
                        resolveChartUpdateParams(chart, columnApi),
                      )
                    })
                  })
                }

                let timeout: NodeJS.Timeout
                const listener = () => {
                  // Debounce to try and figure out when all the columns/rows/filtering/sorting is finally done. There doesn't seem to be better event for this
                  if (timeout) {
                    clearTimeout(timeout)
                  }

                  timeout = setTimeout(() => {
                    updateCharts()
                    gridApi?.removeEventListener(
                      'displayedRowsChanged',
                      listener,
                    )
                  }, 800)
                }

                gridApi?.addEventListener('displayedRowsChanged', listener)
              }

              resolve()
            })
          })
        },
        resetState() {
          const gridApi = currentGridOptions.current?.api
          const columnApi = currentGridOptions.current?.columnApi

          return new Promise((resolve) => {
            requestAnimationFrame(() => {
              gridApi?.resetQuickFilter()
              gridApi?.setFilterModel(null)
              columnApi?.resetColumnState()
              columnApi?.resetColumnGroupState()

              resolve()
            })
          })
        },
      }
    }

    if (datasource) {
      gridApi.setServerSideDatasource(datasource)
    } else {
      if (data?.length) {
        gridApi.setRowData(data)
      }
    }

    return () => {
      gridApi.destroy()
      cleanupColorSchemeListener()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box
      ref={elRef}
      className={[
        'ag-theme-tokenterminal',
        {
          [suppressHeadingCss]: suppressGroupHeader,
        },
      ]}
      style={{
        height,
        ...style,
      }}
      bleedX={bleedX}
      bleedY={bleedY}
    />
  )
}

export const DataTable = memo(
  forwardRef(DataTableInner),
  (prevProps, nextProps) => {
    const propsToCheck = [
      'columnDefs',
      'data',
      'datasource',
      'suppressCsvExport',
      'suppressExcelExport',
      'height',
    ]

    return !propsToCheck.some(
      (prop) =>
        prevProps[prop as keyof DataTableProps<{}>] !==
        nextProps[prop as keyof DataTableProps<{}>],
    )
  },
) as typeof DataTableInner

function getContextMenuItems<T extends unknown>(
  params: GetContextMenuItemsParams<T>,
): Array<MenuItemDef | string> {
  const currentContextItems: Array<string> =
    params.defaultItems?.filter((item) => item !== 'paste') ?? []

  let colDef = params.column?.getColDef()

  // When we're grouping, we want to get the coldef of the original field.
  if (colDef?.showRowGroup) {
    colDef = params.columnApi.getColumn(colDef.showRowGroup)?.getColDef()
  }

  if (colDef && colDef?.getContextMenuItems) {
    return colDef?.getContextMenuItems(
      {
        ...params,
        defaultItems: currentContextItems,
      },
      colDef,
    )
  }

  return currentContextItems
}
